import React, { useEffect, useRef } from 'react';

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const PUNE = { lat: 18.5204, lng: 73.8567 };

const toRad = (d) => (d * Math.PI) / 180;
const toDeg = (r) => (r * 180) / Math.PI;

export function destinationPoint(from, km, bearingDeg) {
  const R = 6371;
  const d = Math.max(0.05, km) / R;
  const lat1 = toRad(from.lat);
  const lon1 = toRad(from.lng);
  const brng = toRad(bearingDeg);
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(brng));
  const lon2 =
    lon1 +
    Math.atan2(
      Math.sin(brng) * Math.sin(d) * Math.cos(lat1),
      Math.cos(d) - Math.sin(lat1) * Math.sin(lat2),
    );
  return { lat: toDeg(lat2), lng: toDeg(lon2) };
}

export function regularHexagon(center, radiusKm) {
  const r = Math.max(1, Math.min(50, Number(radiusKm) || 15));
  return [0, 60, 120, 180, 240, 300].map((deg) => destinationPoint(center, r, deg));
}

export function parseHex(raw, center, radiusKm) {
  if (!Array.isArray(raw) || raw.length !== 6) return regularHexagon(center, radiusKm);
  const pts = raw.map((item) => ({
    lat: Number(item?.lat ?? item?.latitude),
    lng: Number(item?.lng ?? item?.longitude),
  }));
  if (pts.some((p) => !Number.isFinite(p.lat) || !Number.isFinite(p.lng))) {
    return regularHexagon(center, radiusKm);
  }
  return pts;
}

export function serializeHex(hex) {
  return (hex || []).map((p) => ({
    lat: Math.round(Number(p.lat) * 1e6) / 1e6,
    lng: Math.round(Number(p.lng) * 1e6) / 1e6,
  }));
}

export async function geocodePlace(city, area) {
  const q = [area, city, 'India'].filter(Boolean).join(', ');
  if (!q || q === 'India') return null;
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`,
      { headers: { Accept: 'application/json' } },
    );
    const rows = await res.json();
    const row = Array.isArray(rows) ? rows[0] : null;
    const lat = Number(row?.lat);
    const lng = Number(row?.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  } catch {
    return null;
  }
}

const loadLeaflet = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.L) return Promise.resolve(window.L);
  if (window.__roadserveLeafletPromise) return window.__roadserveLeafletPromise;
  window.__roadserveLeafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = LEAFLET_CSS;
      document.head.appendChild(css);
    }
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => (window.L ? resolve(window.L) : reject(new Error('Leaflet failed')));
    script.onerror = () => reject(new Error('Leaflet blocked'));
    document.body.appendChild(script);
  });
  return window.__roadserveLeafletPromise;
};

export default function ServiceAreaMapEditor({ lat, lng, radiusKm, hex, shape = 'circle', onChange }) {
  const wrapRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef({});
  const propsRef = useRef({ lat, lng, radiusKm, hex, shape, onChange });
  propsRef.current = { lat, lng, radiusKm, hex, shape, onChange };

  const center = {
    lat: Number.isFinite(Number(lat)) ? Number(lat) : PUNE.lat,
    lng: Number.isFinite(Number(lng)) ? Number(lng) : PUNE.lng,
  };
  const km = Math.max(1, Math.min(50, Number(radiusKm) || 15));
  const hexPts = Array.isArray(hex) && hex.length === 6 ? hex : regularHexagon(center, km);
  const isHex = shape === 'hex';

  const applyShape = (layers, map, nextShape) => {
    if (!layers || !map) return;
    const hexOn = nextShape === 'hex';
    if (hexOn) {
      if (map.hasLayer(layers.circle)) map.removeLayer(layers.circle);
      if (!map.hasLayer(layers.polygon)) layers.polygon.addTo(map);
      (layers.vertices || []).forEach((v) => {
        if (!map.hasLayer(v)) v.addTo(map);
      });
    } else {
      if (!map.hasLayer(layers.circle)) layers.circle.addTo(map);
      if (map.hasLayer(layers.polygon)) map.removeLayer(layers.polygon);
      (layers.vertices || []).forEach((v) => {
        if (map.hasLayer(v)) map.removeLayer(v);
      });
    }
  };

  useEffect(() => {
    let cancelled = false;
    loadLeaflet()
      .then((L) => {
        if (cancelled || !wrapRef.current || mapRef.current) return;
        const map = L.map(wrapRef.current, {
          zoomControl: true,
          attributionControl: false,
        }).setView([center.lat, center.lng], 12);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);

        const shopIcon = L.divIcon({
          className: '',
          html: '<div style="width:22px;height:22px;border-radius:999px;background:#1B4F9C;border:3px solid #fff;box-shadow:0 2px 8px rgba(15,23,42,.35)"></div>',
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        });
        const vertexIcon = L.divIcon({
          className: '',
          html: '<div style="width:12px;height:12px;border-radius:3px;background:#fff;border:2px solid #E02020;box-shadow:0 1px 4px rgba(15,23,42,.3)"></div>',
          iconSize: [12, 12],
          iconAnchor: [6, 6],
        });

        const circle = L.circle([center.lat, center.lng], {
          radius: km * 1000,
          color: '#1B4F9C',
          weight: 2,
          fillColor: '#1B4F9C',
          fillOpacity: 0.12,
        });

        const polygon = L.polygon(
          hexPts.map((p) => [p.lat, p.lng]),
          {
            color: '#E02020',
            weight: 2,
            dashArray: '5 4',
            fillColor: '#E02020',
            fillOpacity: 0.1,
          },
        );

        const marker = L.marker([center.lat, center.lng], { draggable: true, icon: shopIcon }).addTo(map);

        const vertices = hexPts.map((p) =>
          L.marker([p.lat, p.lng], { draggable: true, icon: vertexIcon, zIndexOffset: 40 }),
        );

        const emit = (nextLat, nextLng, nextHex, nextKm, nextShape) => {
          propsRef.current.onChange?.({
            lat: nextLat,
            lng: nextLng,
            hex: nextHex,
            radiusKm: nextKm ?? propsRef.current.radiusKm,
            shape: nextShape || propsRef.current.shape,
          });
        };

        marker.on('drag', (e) => {
          const { lat: nLat, lng: nLng } = e.target.getLatLng();
          const prev = propsRef.current;
          const dLat = nLat - Number(prev.lat);
          const dLng = nLng - Number(prev.lng);
          const moved = (prev.hex || hexPts).map((p) => ({ lat: p.lat + dLat, lng: p.lng + dLng }));
          circle.setLatLng([nLat, nLng]);
          polygon.setLatLngs(moved.map((p) => [p.lat, p.lng]));
          vertices.forEach((v, i) => v.setLatLng([moved[i].lat, moved[i].lng]));
        });
        marker.on('dragend', (e) => {
          const { lat: nLat, lng: nLng } = e.target.getLatLng();
          const prev = propsRef.current;
          const dLat = nLat - Number(prev.lat);
          const dLng = nLng - Number(prev.lng);
          const moved = (prev.hex || hexPts).map((p) => ({ lat: p.lat + dLat, lng: p.lng + dLng }));
          emit(nLat, nLng, moved);
        });

        vertices.forEach((v, i) => {
          v.on('drag', (e) => {
            const pts = [...(propsRef.current.hex || hexPts)];
            const ll = e.target.getLatLng();
            pts[i] = { lat: ll.lat, lng: ll.lng };
            polygon.setLatLngs(pts.map((p) => [p.lat, p.lng]));
          });
          v.on('dragend', (e) => {
            const pts = [...(propsRef.current.hex || hexPts)];
            const ll = e.target.getLatLng();
            pts[i] = { lat: ll.lat, lng: ll.lng };
            emit(propsRef.current.lat, propsRef.current.lng, pts);
          });
        });

        map.on('click', (e) => {
          const nLat = e.latlng.lat;
          const nLng = e.latlng.lng;
          const prev = propsRef.current;
          const dLat = nLat - Number(prev.lat);
          const dLng = nLng - Number(prev.lng);
          const moved = (prev.hex || hexPts).map((p) => ({ lat: p.lat + dLat, lng: p.lng + dLng }));
          marker.setLatLng([nLat, nLng]);
          circle.setLatLng([nLat, nLng]);
          polygon.setLatLngs(moved.map((p) => [p.lat, p.lng]));
          vertices.forEach((vtx, i) => vtx.setLatLng([moved[i].lat, moved[i].lng]));
          emit(nLat, nLng, moved);
        });

        mapRef.current = map;
        layersRef.current = { L, circle, polygon, marker, vertices };
        applyShape(layersRef.current, map, propsRef.current.shape || 'circle');
        setTimeout(() => map.invalidateSize(), 80);
        setTimeout(() => map.invalidateSize(), 320);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        layersRef.current = {};
      }
    };
  }, []);

  useEffect(() => {
    const layers = layersRef.current;
    const map = mapRef.current;
    if (!layers?.circle || !map) return;
    const nextKm = Math.max(1, Math.min(50, Number(radiusKm) || 15));
    const nextCenter = {
      lat: Number.isFinite(Number(lat)) ? Number(lat) : PUNE.lat,
      lng: Number.isFinite(Number(lng)) ? Number(lng) : PUNE.lng,
    };
    const nextHex = Array.isArray(hex) && hex.length === 6 ? hex : regularHexagon(nextCenter, nextKm);
    layers.circle.setLatLng([nextCenter.lat, nextCenter.lng]);
    layers.circle.setRadius(nextKm * 1000);
    layers.marker.setLatLng([nextCenter.lat, nextCenter.lng]);
    layers.polygon.setLatLngs(nextHex.map((p) => [p.lat, p.lng]));
    (layers.vertices || []).forEach((v, i) => {
      if (nextHex[i]) v.setLatLng([nextHex[i].lat, nextHex[i].lng]);
    });
    applyShape(layers, map, shape === 'hex' ? 'hex' : 'circle');
    map.setView([nextCenter.lat, nextCenter.lng], map.getZoom());
  }, [lat, lng, radiusKm, hex, shape]);

  return (
    <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200">
      <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1.5">
        <button
          type="button"
          onClick={() =>
            onChange?.({
              lat: center.lat,
              lng: center.lng,
              hex: hexPts,
              radiusKm: km,
              shape: 'circle',
            })
          }
          className={`inline-flex flex-1 items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-bold ${
            !isHex ? 'bg-[#1B4F9C] text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200'
          }`}
        >
          <span className={`h-2 w-2 rounded-full ${!isHex ? 'bg-white' : 'bg-[#1B4F9C]'}`} />
          Circle
        </button>
        <button
          type="button"
          onClick={() =>
            onChange?.({
              lat: center.lat,
              lng: center.lng,
              hex: regularHexagon(center, km),
              radiusKm: km,
              shape: 'hex',
            })
          }
          className={`inline-flex flex-1 items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-bold ${
            isHex ? 'bg-[#E02020] text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200'
          }`}
        >
          <span className={`h-2 w-2 rotate-45 rounded-[1px] ${isHex ? 'bg-white' : 'border border-[#E02020]'}`} />
          Hexagon
        </button>
      </div>
      <div ref={wrapRef} className="h-[220px] w-full bg-slate-100" />
    </div>
  );
}

export { PUNE };
